

import { useState } from "react"
import PracticeGenerator from "./PracticeGenerator"
import ConceptVisualizer from "./ConceptVisualizer"
import LessonPlanGenerator from "./LessonPlanGenerator"
import { BookOpen, Brain, Lightbulb, ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react"

const EducationalToolbar = ({ onSubmit }) => {
  const [activeTab, setActiveTab] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const tools = [
    { id: "practice", label: "Practice Generator", icon: <Brain className="h-5 w-5" /> },
    { id: "visualize", label: "Concept Visualizer", icon: <Lightbulb className="h-5 w-5" /> },
    { id: "lesson", label: "Lesson Plan Generator", icon: <BookOpen className="h-5 w-5" /> },
  ]

  const toggleOpen = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setActiveTab(null)
    }
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
    if (!isVisible) {
      setIsOpen(false)
      setActiveTab(null)
    }
  }

  const handleTabClick = (tabId) => {
    if (activeTab === tabId) {
      setActiveTab(null)
    } else {
      setActiveTab(tabId)
      setIsOpen(true)
    }
  }

  // Handle the submission from the educational tools
  // Handle the submission from any educational tool
  const handleToolSubmit = (config) => {
    if (onSubmit) {
      // Log the config to debug
      console.log("EducationalToolbar received config:", config)

      // Pass the config directly to the parent component
      onSubmit(config)

      // Close the sidebar after submitting
      setIsVisible(false)

      // Reset the active tab
      setActiveTab(null)
    }
  }



  return (
    <div
      className={`fixed right-0 top-1/6 transform transition-all duration-300 ease-in-out z-40 ${isVisible ? "translate-x-0" : "translate-x-full"
        }`}
    >
      {/* Toggle visibility button - Enhanced for better visibility when closed */}
      <button
        onClick={toggleVisibility}
        className={`absolute left-0 top-0 transform -translate-x-full ${isVisible
            ? "bg-gray-200 text-white p-2 border-2 border-black rounded-md hover:bg-gray-400 transition-colors"
            : "bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-l-lg shadow-lg hover:shadow-xl border border-white transition-all duration-300 animate-pulse -translate-x-2"
          }`}
        style={{ marginLeft: isVisible ? "0" : "-64px" }}
      >
        {isVisible ? (
          <ChevronRight className="h-8 w-5 text-black" />
        ) : (
          <>
            <ChevronLeft className="h-5 w-5 " />
            <span className="font-medium">Tools</span>
          </>
        )}
      </button>

      <div className="bg-white rounded-l-lg shadow-lg border border-gray-200 overflow-hidden w-[28rem]">
        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-900 to-indigo-800 text-white">
          <h3 className="text-lg font-medium">Educational Tools</h3>
          <div className="flex space-x-2">
            <button onClick={toggleOpen} className="p-1 rounded-full hover:bg-white/20">
              {isOpen ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </button>
            <button onClick={toggleVisibility} className="p-1 rounded-full hover:bg-white/20">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isOpen ? (
          <div className="p-3 border-b border-gray-200">
            <div className="flex flex-wrap gap-2">
              {tools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => handleTabClick(tool.id)}
                  className={`flex items-center justify-center px-4 py-2 rounded-lg text-sm flex-1 ${activeTab === tool.id
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {tool.icon}
                  <span className="ml-2">{tool.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-3 border-b border-gray-200">
            <div className="flex flex-col space-y-2">
              {tools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => handleTabClick(tool.id)}
                  className={`flex items-center px-4 py-2 rounded-lg ${activeTab === tool.id
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {tool.icon}
                  <span className="ml-2">{tool.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {isOpen && activeTab === "practice" && (
          <div className="p-4 max-h-[70vh] overflow-y-auto">
            <PracticeGenerator onSubmit={handleToolSubmit} />
          </div>
        )}

        {isOpen && activeTab === "visualize" && (
          <div className="p-4 max-h-[70vh] overflow-y-auto">
            <ConceptVisualizer onSubmit={handleToolSubmit} />
          </div>
        )}

        {isOpen && activeTab === "lesson" && (
          <div className="p-4 max-h-[70vh] overflow-y-auto">
            <LessonPlanGenerator onSubmit={handleToolSubmit} />
          </div>
        )}
      </div>
    </div>
  )
}

export default EducationalToolbar
